import { FormSearchableSelectInput } from '@folio/components/forms/form-searchable-select-input';
import { useQuery } from '@tanstack/react-query';
import { hiscoxControllerGetActivitiesBySectorQueryOptions } from 'korint-sdk';
import type { Field, SelectOption } from 'korint-sdk/react';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

const emptyArray: string[] = [];
const DEPENDENT_FIELDS: [activity: string, distribution: string][] = [
  ['companyActivity2', 'companyActivityDistribution2'],
  ['companyActivity3', 'companyActivityDistribution3'],
  ['companyActivity4', 'companyActivityDistribution4'],
];

const ALL_ACTIVITY_FIELDS = [
  'companyActivity1',
  'companyActivity2',
  'companyActivity3',
  'companyActivity4',
];

export function HiscoxActivityFormField({ field }: { field: Field }) {
  const { t } = useTranslation();
  const form = useFormContext();
  const activityValues = form.watch(ALL_ACTIVITY_FIELDS) as (string | undefined)[];
  const [companyActivity1, activity2, activity3, activity4] = activityValues;

  const { data: activities = emptyArray, isLoading } = useQuery({
    ...hiscoxControllerGetActivitiesBySectorQueryOptions({
      activity: companyActivity1!,
    }),
    enabled: !!companyActivity1,
    select: data => data.data,
  });

  useEffect(() => {
    if (!companyActivity1) {
      for (const [activity, distribution] of DEPENDENT_FIELDS) {
        form.setValue(activity, null);
        form.setValue(distribution, null);
      }
      return;
    }

    for (const [activity, distribution] of DEPENDENT_FIELDS) {
      const value = form.getValues(activity) as string | undefined;
      if (value && !activities.includes(value)) {
        form.setValue(activity, null);
        form.setValue(distribution, null);
      }
    }
  }, [activities, companyActivity1]);

  // Reset distribution when its paired activity is deleted via "Effacer"
  useEffect(() => {
    for (const [activity, distribution] of DEPENDENT_FIELDS) {
      const activityIndex = ALL_ACTIVITY_FIELDS.indexOf(activity);
      if (!activityValues[activityIndex]) {
        const currentDistribution = form.getValues(distribution);
        if (currentDistribution != null) {
          form.setValue(distribution, null, { shouldDirty: true });
        }
      }
    }
  }, [activity2, activity3, activity4]);

  // Filter out activities already selected in earlier fields
  const currentIndex = ALL_ACTIVITY_FIELDS.indexOf(field.name);
  const selectedByPrevious = new Set(
    ALL_ACTIVITY_FIELDS.slice(0, currentIndex)
      .map((_, i) => activityValues[i])
      .filter(Boolean),
  );
  const availableActivities = activities.filter(a => !selectedByPrevious.has(a));
  const noOptions = !isLoading && !!companyActivity1 && availableActivities.length === 0;

  return (
    <FormSearchableSelectInput
      field={{
        ...field,
        type: 'select',
        options: availableActivities.map(value => ({ label: value, value })) as [
          SelectOption,
          ...SelectOption[],
        ],
        disabled: !companyActivity1 || noOptions,
        DEPRECATED_placeholder: noOptions
          ? t('fields.hiscox.noActivityAvailable')
          : field.DEPRECATED_placeholder,
      }}
      isLoading={isLoading}
    />
  );
}
